import { memo } from 'preact/compat'
import { useState, useEffect } from 'preact/hooks'
import { getStylesFn } from 'core/helpers/styles'
import { ElementImageFeatureOptions } from 'core/types/element/element_image_feature'
import optionsPropTypes from 'PropsSpec'

const extractStyles = getStylesFn(['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'], {
  transition: 'opacity 90ms',
  display: 'flex'
})

const extractTextWrapperStyles = getStylesFn([], {
  display: 'flex',
  flexDirection: 'column',
  padding: '0 10px'
})

const extractTextHeadStyles = getStylesFn(
  ['color', 'textAlign', 'lineHeight', 'textTransform', 'letterSpacing', 'fontFamily', 'fontSize'],
  {
    marginTop: 10,
    marginBottom: 6
  }
)

const extractTextBodyStyles = getStylesFn([
  'color',
  'textAlign',
  'lineHeight',
  'textTransform',
  'letterSpacing',
  'fontFamily',
  'fontSize'
])

const extractImgStyles = getStylesFn(
  [
    'borderTop',
    'borderRight',
    'borderBottom',
    'borderLeft',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderBottomRightRadius',
    'borderBottomLeftRadius'
  ],
  {
    maxWidth: '100%'
  }
)

type Props = {
  options: ElementImageFeatureOptions
}

const Image = (props: Props) => {
  const { options } = props
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    const intervalId = setTimeout(() => {
      setLoaded(true)
    }, 500)

    return () => clearTimeout(intervalId)
  }, [options.url])

  const onImageLoad = () => {
    setLoaded(true)
  }

  const styles = {
    ...extractStyles(options),
    flexDirection: options.image.position === 'left' ? 'row' : 'row-reverse',
    width: '100%',
    opacity: loaded ? 1 : 0
  }

  const imageWrapperStyles = {
    width: `${options.column_sizes[0]}%`,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: options.image.position === 'left' ? 'flex-start' : 'flex-end'
  }

  const textStyles = {
    ...extractTextWrapperStyles({}),
    width: `${options.column_sizes[1]}%`
  }

  const headStyles = extractTextHeadStyles(options.head)
  const bodyStyles = extractTextBodyStyles(options.body)

  const imgStyles = {
    ...extractImgStyles(options.image),
    maxHeight: `${options.image.max_height}px`
  }

  return (
    <div style={styles} className="wf_reset wf_image_feature_wrapper">
      <div style={imageWrapperStyles} className="wf_reset wf_image_wrapper">
        <img
          onLoad={onImageLoad}
          style={imgStyles}
          className="wf_reset wf_image"
          src={options.url}
          width={options.image.width}
          height={options.image.height}
        />
      </div>
      <div style={textStyles} className="wf_reset wf_text_wrapper">
        <div style={headStyles} className="wf_reset wf_text_head_wrapper">
          {options.content_head}
        </div>
        <div style={bodyStyles} className="wf_reset wf_text_body_wrapper">
          {options.content_body}
        </div>
      </div>
    </div>
  )
}

Image.propTypes = {
  options: optionsPropTypes('image_feature')
}

export default memo(Image)
